import React, { useEffect, useRef, useState } from "react";
import { Trash2, Plus, Upload, Star, Download } from "lucide-react";
import { adminApi } from "../api/adminApi";
import { getAdminToken } from "../../../helper/adminAuthStorage";
import RatePlans from "./RatePlans";
import AvailabilityCalendar from "./AvailabilityCalendar";
import HelpTip from "./HelpTip";
import "./hotelInventory.css";

const SELECT_FIELDS = [
  ["roomClass", "Room class", "roomClasses"],
  ["qualityTier", "Quality tier", "qualityTiers"],
  ["bedSetup", "Bed setup", "bedSetups"],
  ["bathroom", "Bathroom", "bathroomTypes"],
  ["balcony", "Balcony", "balconyOptions"],
  ["floorType", "Floor type", "floorTypes"],
  ["view", "View", "views"],
];
const NUM_FIELDS = [
  ["bedrooms", "Bedrooms"],
  ["sleepingCapacity", "Sleeping capacity"],
  ["maxAdults", "Max adults"],
  ["maxChildren", "Max children"],
  ["roomSize", "Room size (sqm)"],
  ["physicalRooms", "Physical rooms (ceiling)"],
];

const emptyRoom = () => ({
  name: "", roomClass: "", qualityTier: "", bedSetup: "", bedrooms: "", sleepingCapacity: "",
  maxAdults: "", maxChildren: "", roomSize: "", roomSizeUnit: "sqm", bathroom: "", balcony: "",
  floorType: "", view: "", amenities: [], physicalRooms: "", images: [],
});

const SAMPLE_ROOMS_CSV = `name,roomClass,qualityTier,bedSetup,bedrooms,sleepingCapacity,maxAdults,maxChildren,roomSize,roomSizeUnit,bathroom,balcony,floorType,view,physicalRooms,amenities
Deluxe King Room,Deluxe,Premium,1 King,1,2,2,1,32,sqm,Ensuite,Balcony,Carpeted,Sea View,5,"Free WiFi, Minibar, Safe"
Twin Standard,Standard,Standard,2 Twin,1,2,2,0,26,sqm,Private,None,Tiled,City View,8,"Free WiFi, Tea/Coffee"
Family Suite,Suite,Luxury,1 King + 1 Sofa Bed,2,4,2,2,55,sqm,Private + Bathtub,Terrace,Wooden,Pool View,3,"Free WiFi, Kitchenette, Minibar"
`;

const RoomTypes = ({ hotelId }) => {
  const fileRef = useRef(null);
  const csvRef = useRef(null);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [sd, setSd] = useState({});
  const [amInput, setAmInput] = useState("");
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [ratePlansFor, setRatePlansFor] = useState(null);
  const [calendarFor, setCalendarFor] = useState(null);
  const [toast, setToast] = useState(null);
  const [sel, setSel] = useState([]);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const flash = (type, text) => setToast({ type, text });
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2200);
    return () => clearTimeout(t);
  }, [toast]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminApi("GET", `/admin/inventory/rooms?hotelId=${hotelId}`);
      setRooms(res?.data?.data?.rooms || []);
      setSel([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    (async () => {
      try {
        const res = await adminApi("GET", "/admin/inventory/room-static-data");
        setSd(res?.data?.data || {});
      } catch {
        /* non-blocking */
      }
    })();
  }, [hotelId]);

  const setE = (patch) => setEditing((r) => ({ ...r, ...patch }));
  const clearErr = (k) => setErrors((x) => (x[k] ? { ...x, [k]: false } : x));
  const setNum = (k, v) => {
    if (v === "") { setE({ [k]: "" }); return; }
    if (Number(v) < 0) return;
    setE({ [k]: v });
  };
  const blockMinus = (e) => { if (["-", "+", "e", "E"].includes(e.key)) e.preventDefault(); };

  const save = async () => {
    if (saving) return;
    const errs = {};
    if (!editing.name?.trim()) errs.name = true;
    if (!(Number(editing.physicalRooms) > 0)) errs.physicalRooms = true;
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSaving(true);
    try {
      const payload = { ...editing, hotelId };
      if (editing._id) await adminApi("POST", `/admin/inventory/rooms/${editing._id}`, payload);
      else await adminApi("POST", "/admin/inventory/rooms", payload);
      flash("ok", "Saved");
      load();
      setEditing(null);
    } catch (e) {
      flash("err", e?.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const del = async (r) => {
    if (!window.confirm(`Delete "${r.name}"? This also removes its rate plans and availability.`)) return;
    try {
      await adminApi("POST", `/admin/inventory/rooms/${r._id}/delete`);
      load();
    } catch {
      flash("err", "Delete failed");
    }
  };

  const toggleSel = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const bulkDelete = async () => {
    if (!sel.length) return;
    if (!window.confirm(`Delete ${sel.length} room type(s)? This also removes their rate plans, availability and images.`)) return;
    try {
      for (const id of sel) {
        await adminApi("POST", `/admin/inventory/rooms/${id}/delete`);
      }
      flash("ok", "Deleted");
      load();
    } catch {
      flash("err", "Some deletions failed");
      load();
    }
  };

  const addAmenity = () => {
    const v = amInput.trim();
    if (!v) return;
    setE({ amenities: [...(editing.amenities || []), v] });
    setAmInput("");
  };

  const downloadSampleRooms = () => {
    const blob = new Blob([SAMPLE_ROOMS_CSV], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "rooms-import-sample.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const onImportCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("hotelId", hotelId);
      const resp = await fetch(`${process.env.client_apiEndpoint}/admin/inventory/rooms/import`, {
        method: "POST",
        headers: { Authorization: getAdminToken() },
        body: fd,
      });
      const json = await resp.json();
      if (!resp.ok) throw new Error(json?.message || "Import failed");
      const d = json?.data || {};
      setImportResult(d);
      flash("ok", `Imported ${d.created} of ${d.total}`);
      load();
    } catch (err) {
      flash("err", err.message || "Import failed");
    } finally {
      setImporting(false);
      if (csvRef.current) csvRef.current.value = "";
    }
  };

  const onPickFiles = async (e) => {
    const files = e.target.files;
    if (!files?.length || !editing?._id) return;
    setUploading(true);
    try {
      const fd = new FormData();
      Array.from(files).forEach((f) => fd.append("images", f));
      const resp = await fetch(`${process.env.client_apiEndpoint}/admin/inventory/rooms/${editing._id}/images`, {
        method: "POST", headers: { Authorization: getAdminToken() }, body: fd,
      });
      if (!resp.ok) throw new Error("upload failed");
      const json = await resp.json();
      setE({ images: json?.data?.images || [] });
      flash("ok", "Images uploaded");
    } catch {
      flash("err", "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const setCover = async (key) => {
    const images = (editing.images || []).map((i) => ({ ...i, isCover: i.key === key }));
    setE({ images });
    try { await adminApi("POST", `/admin/inventory/rooms/${editing._id}`, { images }); } catch { flash("err", "Could not set cover"); }
  };
  const deleteImage = async (key) => {
    try {
      const res = await adminApi("POST", `/admin/inventory/rooms/${editing._id}/images/delete`, { key });
      setE({ images: res?.data?.data?.images || [] });
    } catch { flash("err", "Could not remove image"); }
  };

  const total = rooms.length;
  const pageCount = Math.max(1, Math.ceil(total / perPage));
  const safePage = Math.min(page, pageCount);
  const pageStart = (safePage - 1) * perPage;
  const pagedRooms = rooms.slice(pageStart, pageStart + perPage);

  return (
    <div>
      {toast ? <div className={`toast ${toast.type}`}>{toast.text}</div> : null}
      <div className="hi-inline" style={{ marginBottom: 14 }}>
        <button className="btn primary" onClick={() => { setEditing(emptyRoom()); setAmInput(""); setErrors({}); }}>
          <Plus size={15} style={{ marginRight: 6, verticalAlign: "-2px" }} />Add room type
        </button>
        <button className="btn ghost" onClick={downloadSampleRooms}>
          <Download size={15} style={{ marginRight: 6, verticalAlign: "-2px" }} />Sample CSV
        </button>
        <input ref={csvRef} type="file" accept=".csv,text/csv" hidden onChange={onImportCsv} />
        <button className="btn soft" onClick={() => csvRef.current?.click()} disabled={importing}>
          <Upload size={15} style={{ marginRight: 6, verticalAlign: "-2px" }} />{importing ? "Importing…" : "Import CSV"}
        </button>
        {sel.length > 0 ? <button className="btn ghost danger" onClick={bulkDelete}>Delete Selected ({sel.length})</button> : null}
        <HelpTip title="Rooms, rate plans, prices & availability">
          <ul>
            <li><strong>Room type</strong> = a category (e.g. Deluxe King) with its capacity, beds and photos.</li>
            <li><strong>Rate plans</strong> = the deals sold on that room (Room Only, Breakfast…), each with its own price, tax and cancellation. Open with the <strong>Rate plans</strong> button.</li>
            <li><strong>Prices</strong> = the nightly rate for a rate plan. Inside Rate plans, click <strong>Prices</strong>; use Bulk edit to paint a whole season at once.</li>
            <li><strong>Availability</strong> = how many rooms are open to sell each night — shared by all the room's rate plans. Open with the <strong>Availability</strong> button.</li>
          </ul>
        </HelpTip>
      </div>

      {importResult && (
        <div className="hi-section" style={{ borderColor: (importResult.results || []).some((r) => r.status === "error") ? "#f3d19e" : "#bfe6cd" }}>
          <div style={{ fontWeight: 700 }}>Imported {importResult.created} of {importResult.total} rooms.</div>
          {(importResult.results || []).filter((r) => r.status === "error").map((r) => (
            <div key={r.row} className="hi-sub" style={{ color: "#c33" }}>Row {r.row}: {r.message}</div>
          ))}
          <button className="btn ghost" style={{ marginTop: 8 }} onClick={() => setImportResult(null)}>Dismiss</button>
        </div>
      )}

      {loading ? (
        <div className="hi-empty">Loading…</div>
      ) : rooms.length === 0 ? (
        <div className="hi-empty">No room types yet.</div>
      ) : (
        pagedRooms.map((r) => (
          <div className="hi-list-row" key={r._id}>
            <input type="checkbox" checked={sel.includes(r._id)} onChange={() => toggleSel(r._id)} />
            {r.coverImage ? <img className="hi-thumb" src={r.coverImage} alt="" /> : <div className="hi-thumb" />}
            <div className="grow">
              <div className="rn">{r.name}</div>
              <div className="rm">{[r.roomClass, r.bedSetup, r.view].filter(Boolean).join(" · ") || "—"} · {r.physicalRooms || 0} rooms</div>
            </div>
            <button className="btn ghost" onClick={() => setRatePlansFor(r)}>Rate plans</button>
            <button className="btn ghost" onClick={() => setCalendarFor(r)}>Availability</button>
            <button className="btn ghost" onClick={() => { setEditing({ ...emptyRoom(), ...r }); setAmInput(""); setErrors({}); }}>Edit</button>
            <button className="hi-mini del" onClick={() => del(r)}><Trash2 size={15} /></button>
          </div>
        ))
      )}

      {!loading && total > 0 ? (
        <div className="hi-pager">
          <div className="hi-pager__rows">
            <span>Rows per page</span>
            <select
              className="inp"
              value={perPage}
              onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
            >
              {[10, 20, 30].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div className="hi-pager__info">
            {pageStart + 1}–{Math.min(pageStart + perPage, total)} of {total}
          </div>
          <div className="hi-pager__nav">
            <button className="btn ghost" disabled={safePage <= 1} onClick={() => setPage(safePage - 1)}>Prev</button>
            <span className="hi-pager__page">Page {safePage} / {pageCount}</span>
            <button className="btn ghost" disabled={safePage >= pageCount} onClick={() => setPage(safePage + 1)}>Next</button>
          </div>
        </div>
      ) : null}

      {editing && (
        <div className="hi-modal-ov" onMouseDown={(e) => { if (e.target === e.currentTarget) setEditing(null); }}>
          <div className="hi-modal">
            <div className="hi-modal-head">
              <h3>{editing._id ? "Edit room type" : "New room type"}</h3>
              <button className="hi-x" onClick={() => setEditing(null)}>×</button>
            </div>
            <div className="hi-modal-body">
              <div className="hi-grid">
                <div className="hi-fld"><label>Room name *</label><input className={`inp${errors.name ? " err" : ""}`} value={editing.name} onChange={(e) => { setE({ name: e.target.value }); clearErr("name"); }} />{errors.name ? <span className="hi-err">Room name is required</span> : null}</div>
                {SELECT_FIELDS.map(([k, label, opt]) => (
                  <div className="hi-fld" key={k}>
                    <label>{label}</label>
                    <select className="inp" value={editing[k] || ""} onChange={(e) => setE({ [k]: e.target.value })}>
                      <option value="">Select…</option>
                      {(sd[opt] || []).map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                ))}
                {NUM_FIELDS.map(([k, label]) => {
                  const req = k === "physicalRooms";
                  return (
                    <div className="hi-fld" key={k}>
                      <label>{label}{req ? " *" : ""}</label>
                      <input className={`inp${req && errors[k] ? " err" : ""}`} type="number" min="0" value={editing[k]} onKeyDown={blockMinus} onChange={(e) => { setNum(k, e.target.value); if (req) clearErr(k); }} />
                      {req && errors[k] ? <span className="hi-err">Physical rooms is required — it sets how many rooms you can open to sell.</span> : null}
                    </div>
                  );
                })}
              </div>

              <div className="hi-fld" style={{ marginTop: 14 }}>
                <label>Room amenities</label>
                <div className="hi-inline">
                  <input className="inp" placeholder="Add amenity…" value={amInput} onChange={(e) => setAmInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addAmenity(); } }} />
                  <button className="btn ghost" onClick={addAmenity}>Add</button>
                </div>
                <div className="hi-taglist" style={{ marginTop: 8 }}>
                  {(editing.amenities || []).map((a, i) => (
                    <span className="hi-tag" key={i}>{a}<button onClick={() => setE({ amenities: editing.amenities.filter((_, idx) => idx !== i) })}>×</button></span>
                  ))}
                </div>
              </div>

              <div className="hi-section" style={{ marginTop: 16 }}>
                <h3>Photos</h3>
                {!editing._id ? (
                  <div className="hi-sub">Save the room first to add photos.</div>
                ) : (
                  <>
                    <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={onPickFiles} />
                    <button className="btn soft" onClick={() => fileRef.current?.click()} disabled={uploading}>
                      <Upload size={15} style={{ marginRight: 6, verticalAlign: "-2px" }} />{uploading ? "Uploading…" : "Upload images"}
                    </button>
                    <div className="hi-gallery" style={{ marginTop: 12 }}>
                      {(editing.images || []).map((img) => (
                        <div className="hi-imgcard" key={img.key}>
                          <img src={img.url} alt="" />
                          <div className="bar">
                            {img.isCover ? <span className="cover">Cover</span> : <button className="hi-mini set" onClick={() => setCover(img.key)}><Star size={13} /> Cover</button>}
                            <button className="hi-mini del" onClick={() => deleteImage(img.key)}><Trash2 size={13} /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="hi-modal-foot">
              <button className="btn ghost" onClick={() => setEditing(null)} disabled={saving}>Close</button>
              <button className="btn primary" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save room"}</button>
            </div>
          </div>
        </div>
      )}

      {ratePlansFor && (
        <RatePlans roomTypeId={ratePlansFor._id} roomName={ratePlansFor.name} onClose={() => setRatePlansFor(null)} />
      )}

      {calendarFor && (
        <AvailabilityCalendar roomTypeId={calendarFor._id} roomName={calendarFor.name} onClose={() => setCalendarFor(null)} />
      )}
    </div>
  );
};

export default RoomTypes;
